import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon, PlayCircleIcon, PhotoIcon } from '../../Icons';
import { useSwipeGesture } from '../../../hooks/useSwipeGesture';

interface SharedPropertyGalleryProps {
    images: string[];
    video: { embedUrl: string } | null;
    activeIndex: number;
    onChangeIndex: (index: number) => void;
}

export const SharedPropertyGallery: React.FC<SharedPropertyGalleryProps> = ({ images, video, activeIndex, onChangeIndex }) => {
    const totalItems = images.length + (video ? 1 : 0);
    const showVideo = activeIndex === -1 && video;
    
    const handleNext = () => {
        if (activeIndex === images.length - 1) onChangeIndex(video ? -1 : 0);
        else onChangeIndex(activeIndex + 1);
    };

    const handlePrev = () => {
        if (activeIndex === (video ? -1 : 0)) onChangeIndex(images.length - 1);
        else onChangeIndex(activeIndex - 1);
    };

    const { containerProps } = useSwipeGesture({
        onSwipeLeft: handleNext,
        onSwipeRight: handlePrev,
    });

    const currentNumber = showVideo ? 1 : activeIndex + 1;

    return (
        <section className="space-y-3">
            <div 
                className="relative aspect-[16/9] bg-gray-900 rounded-xl overflow-hidden group shadow-2xl touch-pan-y select-none"
                {...containerProps}
            >
                {showVideo ? (
                    <iframe className="w-full h-full" src={video.embedUrl} frameBorder="0" allowFullScreen></iframe>
                ) : images[activeIndex] ? (
                    <img 
                        src={images[activeIndex]} 
                        alt="Propiedad" 
                        className="w-full h-full object-contain bg-gray-900 pointer-events-none select-none" 
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-500"><PhotoIcon className="w-20 h-20" /></div>
                )}

                {totalItems > 1 && (
                    <>
                        <button 
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handlePrev(); }} 
                            aria-label="Imagen anterior"
                            className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 p-2 sm:p-2.5 bg-black/55 hover:bg-black/85 text-white rounded-full shadow-lg transition-all z-20 opacity-90 sm:opacity-80 sm:hover:opacity-100 backdrop-blur-md cursor-pointer active:scale-95"
                        >
                            <ChevronLeftIcon className="w-5 h-5" />
                        </button>
                        <button 
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleNext(); }} 
                            aria-label="Imagen siguiente"
                            className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 p-2 sm:p-2.5 bg-black/55 hover:bg-black/85 text-white rounded-full shadow-lg transition-all z-20 opacity-90 sm:opacity-80 sm:hover:opacity-100 backdrop-blur-md cursor-pointer active:scale-95"
                        >
                            <ChevronRightIcon className="w-5 h-5" />
                        </button>
                    </>
                )}

                {/* Contador discreto */}
                {totalItems > 0 && (
                    <div className="absolute top-3 right-3 z-10 px-3 py-1 bg-black/60 backdrop-blur-md text-white text-[11px] font-bold rounded-full border border-white/10 shadow-sm">
                        {showVideo ? `Video 1 / ${totalItems}` : `Imagen ${currentNumber} / ${images.length}`}
                    </div>
                )}
            </div>

            {/* Puntos de navegación discretos */}
            {totalItems > 1 && (
                <div className="flex items-center justify-center gap-1.5 py-2 overflow-x-auto custom-scrollbar">
                    {video && (
                        <button 
                            onClick={() => onChangeIndex(-1)} 
                            className={`flex items-center justify-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold transition-all ${
                                activeIndex === -1 ? 'bg-sky-600 text-white scale-105 shadow' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            <PlayCircleIcon className="w-3.5 h-3.5" />
                            <span>Video</span>
                        </button>
                    )}
                    {images.map((_, i) => (
                        <button 
                            key={i} 
                            onClick={() => onChangeIndex(i)} 
                            className={`transition-all rounded-full ${
                                activeIndex === i ? 'w-6 h-2 bg-sky-600' : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                            }`}
                        />
                    ))}
                </div>
            )}
        </section>
    );
};
